import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Monitoring.css';

const DEFAULT_THRESHOLD_DAYS = 14;
const MANUAL_TYPES = ['SAML', 'Certificate', 'Secret', 'Other'];

function createUniqueAppId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function emptyManualForm() {
  return {
    application: '',
    appId: createUniqueAppId(),
    type: 'Certificate',
    expiry: ''
  };
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDate(value);
  return date ? date.toLocaleDateString() : '—';
}

function isManualSource(source) {
  const raw = String(source || '').trim().toLowerCase();
  return raw === 'manual' || raw === 'manually';
}

function displaySource(source) {
  const raw = String(source || '').trim();
  if (!raw) return '—';
  if (raw.toLowerCase() === 'manual' || raw.toLowerCase() === 'manually') return 'Manually';
  if (raw.toLowerCase() === 'msgraph') return 'Microsoft Graph';
  return raw;
}

function expiryTone(daysLeft, threshold) {
  if (daysLeft == null || Number.isNaN(Number(daysLeft))) return 'unknown';
  const days = Number(daysLeft);
  if (days < 0) return 'expired';
  const limit = threshold == null || Number.isNaN(Number(threshold)) ? DEFAULT_THRESHOLD_DAYS : Number(threshold);
  if (days <= limit) return 'warning';
  return 'ok';
}

function groupMonitorRows(rows) {
  const groups = new Map();

  (rows || []).forEach((row) => {
    const appId = String(row.appId || '').trim();
    const key = appId ? appId.toLowerCase() : `row:${row.id}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        appId,
        application: row.application || 'Untitled application',
        rows: []
      });
    }
    const group = groups.get(key);
    group.rows.push(row);
    if ((!group.application || group.application === 'Untitled application') && row.application) {
      group.application = row.application;
    }
    if (!group.appId && appId) {
      group.appId = appId;
    }
  });

  return Array.from(groups.values()).map((group) => {
    const daysLeftValues = group.rows
      .map((row) => row.daysLeft)
      .filter((value) => value != null && !Number.isNaN(Number(value)))
      .map(Number);
    const earliestDaysLeft = daysLeftValues.length ? Math.min(...daysLeftValues) : null;
    const expiryDates = group.rows
      .map((row) => parseDate(row.expiry))
      .filter(Boolean);
    const earliestExpiry = expiryDates.length
      ? new Date(Math.min(...expiryDates.map((date) => date.getTime())))
      : null;
    const alerts = group.rows.map((row) => Boolean(row.alert));
    const monitoringOn = alerts.length > 0 && alerts.every(Boolean);
    const monitoringOff = alerts.every((value) => !value);
    const thresholds = group.rows
      .map((row) => row.threshold)
      .filter((value) => value != null && !Number.isNaN(Number(value)))
      .map(Number);
    const threshold = thresholds.length ? thresholds[0] : null;

    return {
      ...group,
      earliestExpiry,
      earliestDaysLeft,
      monitoringOn,
      monitoringMixed: !monitoringOn && !monitoringOff,
      threshold,
      credentialCount: group.rows.length,
      isManual: group.rows.length > 0 && group.rows.every((row) => isManualSource(row.source))
    };
  }).sort((left, right) => {
    if (left.earliestDaysLeft == null && right.earliestDaysLeft == null) {
      return left.application.localeCompare(right.application, undefined, { sensitivity: 'base' });
    }
    if (left.earliestDaysLeft == null) return 1;
    if (right.earliestDaysLeft == null) return -1;
    if (left.earliestDaysLeft !== right.earliestDaysLeft) {
      return left.earliestDaysLeft - right.earliestDaysLeft;
    }
    return left.application.localeCompare(right.application, undefined, { sensitivity: 'base' });
  });
}

const Monitoring = () => {
  const { selectedTenant, authenticatedFetch, hasPrivilege } = useAuth();
  const canManage = hasPrivilege('monitoring', 'update');
  const canViewSimple = hasPrivilege('monitoring') || canManage;
  const [activeView, setActiveView] = useState(canManage ? 'setup' : 'monitored');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [updatingKey, setUpdatingKey] = useState(null);
  const [thresholdModal, setThresholdModal] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [manualError, setManualError] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!canManage && activeView !== 'monitored') {
      setActiveView('monitored');
    }
  }, [canManage, activeView]);

  const fetchRows = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/monitor?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch monitoring data');
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError('Error fetching monitoring data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const groups = useMemo(() => groupMonitorRows(rows), [rows]);
  const isSetupView = canManage && activeView === 'setup';

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return groups.filter((group) => {
      if (!isSetupView && !group.rows.some((row) => row.alert)) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        group.application,
        group.appId,
        ...group.rows.map((row) => row.credential),
        ...group.rows.map((row) => row.type),
        ...group.rows.map((row) => row.source)
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [groups, isSetupView, search]);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applyMonitoring = async (group, alert, threshold) => {
    if (!selectedTenant || !group) return;
    try {
      setUpdatingKey(group.key);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/monitor/alerts?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appId: group.appId || null,
            ids: group.rows.map((row) => row.id),
            alert,
            threshold
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to update monitoring');
        return;
      }
      setRows((prev) => prev.map((row) => {
        const matchesApp = group.appId
          ? String(row.appId || '').trim().toLowerCase() === group.appId.toLowerCase()
          : group.rows.some((item) => item.id === row.id);
        if (!matchesApp) return row;
        return {
          ...row,
          alert,
          threshold: alert && threshold != null ? Number(threshold) : row.threshold
        };
      }));
    } catch (err) {
      console.error('Error updating monitoring:', err);
      setError('Error updating monitoring');
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleToggleMonitoring = (group) => {
    if (!canManage || updatingKey === group.key) return;
    if (group.monitoringOn) {
      applyMonitoring(group, false);
      return;
    }
    setThresholdModal({
      group,
      days: String(group.threshold ?? DEFAULT_THRESHOLD_DAYS),
      mode: 'enable',
      error: ''
    });
  };

  const handleEditThreshold = (group) => {
    if (!canManage) return;
    if (!group.monitoringOn && !group.monitoringMixed) {
      handleToggleMonitoring(group);
      return;
    }
    setThresholdModal({
      group,
      days: String(group.threshold ?? DEFAULT_THRESHOLD_DAYS),
      mode: 'threshold',
      error: ''
    });
  };

  const submitThresholdModal = async (event) => {
    event.preventDefault();
    if (!thresholdModal?.group) return;
    const parsed = Number.parseInt(String(thresholdModal.days).trim(), 10);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setThresholdModal((prev) => ({ ...prev, error: 'Enter a valid number of days (0 or more).' }));
      return;
    }
    const group = thresholdModal.group;
    setThresholdModal(null);
    await applyMonitoring(group, true, parsed);
  };

  const openManualModal = () => {
    setManualForm(emptyManualForm());
    setManualError('');
    setShowManualModal(true);
  };

  const submitManualEntry = async (event) => {
    event.preventDefault();
    if (!selectedTenant) return;
    const application = manualForm.application.trim();
    const expiry = manualForm.expiry.trim();
    if (!application) {
      setManualError('Application name is required.');
      return;
    }
    if (!expiry) {
      setManualError('Expiry date is required.');
      return;
    }
    try {
      setManualSaving(true);
      setManualError('');
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/monitor/manual?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application,
            appId: manualForm.appId,
            type: manualForm.type,
            expiry
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setManualError(data.error || 'Failed to add manual entry');
        return;
      }
      if (data.row) {
        setRows((prev) => [...prev, data.row]);
      } else {
        await fetchRows();
      }
      setShowManualModal(false);
    } catch (err) {
      console.error('Error adding manual monitor entry:', err);
      setManualError('Error adding manual entry');
    } finally {
      setManualSaving(false);
    }
  };

  const matchesGroup = (row, group) => {
    if (!group) return false;
    if (group.appId) {
      return String(row.appId || '').trim().toLowerCase() === group.appId.toLowerCase();
    }
    return group.rows.some((item) => item.id === row.id);
  };

  const confirmDeleteService = (group) => {
    if (!canManage || !group?.isManual || updatingKey === group.key) return;
    setDeleteModal({ group, error: '' });
  };

  const submitDeleteService = async (event) => {
    event.preventDefault();
    const group = deleteModal?.group;
    if (!selectedTenant || !group) return;
    try {
      setDeleting(true);
      setDeleteModal((prev) => (prev ? { ...prev, error: '' } : prev));
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/monitor/manual?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appId: group.appId || null,
            ids: group.rows.map((row) => row.id)
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setDeleteModal((prev) => ({
          ...(prev || { group }),
          error: data.error || 'Failed to delete service'
        }));
        return;
      }
      setRows((prev) => prev.filter((row) => !matchesGroup(row, group)));
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.delete(group.key);
        return next;
      });
      setDeleteModal(null);
    } catch (err) {
      console.error('Error deleting monitor service:', err);
      setDeleteModal((prev) => ({
        ...(prev || { group }),
        error: 'Error deleting service'
      }));
    } finally {
      setDeleting(false);
    }
  };

  const renderCredentialRows = (group) => {
    const credentials = isSetupView ? group.rows : group.rows.filter((row) => row.alert);
    if (!credentials.length) {
      return <div className="monitoring-empty nested">No credentials for this application.</div>;
    }

    return (
      <div className="monitoring-nested">
        <table className="monitoring-table nested">
          <thead>
            <tr>
              {isSetupView && <th>Key / credential</th>}
              <th>Type</th>
              <th>Start</th>
              <th>Expiry</th>
              <th>Days left</th>
              <th>Last used</th>
              {isSetupView && <th>Source</th>}
              {isSetupView && <th>Threshold</th>}
            </tr>
          </thead>
          <tbody>
            {credentials.map((row) => {
              const tone = expiryTone(row.daysLeft, row.threshold);
              return (
                <tr key={row.id}>
                  {isSetupView && <td>{row.credential || '—'}</td>}
                  <td>{row.type || '—'}</td>
                  <td>{formatDate(row.start)}</td>
                  <td>{formatDate(row.expiry)}</td>
                  <td>
                    <span className={`monitoring-days ${tone}`}>
                      {row.daysLeft == null ? '—' : row.daysLeft}
                    </span>
                  </td>
                  <td>{formatDate(row.lastUsed)}</td>
                  {isSetupView && <td>{displaySource(row.source)}</td>}
                  {isSetupView && <td>{row.threshold == null ? '—' : `${row.threshold} days`}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGroups = () => {
    if (loading) {
      return <div className="monitoring-loading">Loading monitoring data...</div>;
    }
    if (error) {
      return <div className="monitoring-error">{error}</div>;
    }
    if (!visibleGroups.length) {
      return (
        <div className="monitoring-empty">
          {isSetupView
            ? 'No applications found. Graph credentials appear after the monitor service runs, or add a manual entry.'
            : 'No applications are currently monitored.'}
        </div>
      );
    }

    return (
      <div className="monitoring-groups">
        {visibleGroups.map((group) => {
          const expanded = expandedKeys.has(group.key);
          const tone = expiryTone(group.earliestDaysLeft, group.threshold);
          return (
            <div key={group.key} className={`monitoring-group ${expanded ? 'expanded' : ''}`}>
              <div className="monitoring-group-header">
                <button
                  type="button"
                  className="monitoring-expand"
                  onClick={() => toggleExpanded(group.key)}
                  aria-expanded={expanded}
                >
                  <span className="monitoring-chevron">{expanded ? '▼' : '▶'}</span>
                  <span className="monitoring-app-name">{group.application}</span>
                  <span className="monitoring-count">{group.credentialCount}</span>
                </button>
                <div className="monitoring-group-meta">
                  <div className="monitoring-meta-item">
                    <span className="monitoring-meta-label">Earliest expiry</span>
                    <span className={`monitoring-meta-value monitoring-days ${tone}`}>
                      {formatDate(group.earliestExpiry)}
                      {group.earliestDaysLeft != null ? ` (${group.earliestDaysLeft} days)` : ''}
                    </span>
                  </div>
                  {isSetupView && (
                    <>
                      <div className="monitoring-meta-item">
                        <span className="monitoring-meta-label">Threshold</span>
                        <button
                          type="button"
                          className="monitoring-threshold-button"
                          onClick={() => handleEditThreshold(group)}
                          disabled={updatingKey === group.key}
                        >
                          {group.threshold == null ? 'Set days' : `${group.threshold} days`}
                        </button>
                      </div>
                      <button
                        type="button"
                        className={`monitoring-toggle ${group.monitoringOn ? 'on' : group.monitoringMixed ? 'mixed' : 'off'}`}
                        onClick={() => handleToggleMonitoring(group)}
                        disabled={updatingKey === group.key}
                      >
                        <span className="monitoring-toggle-thumb" />
                        <span className="monitoring-toggle-label">
                          {group.monitoringOn ? 'Monitoring on' : group.monitoringMixed ? 'Partial' : 'Monitoring off'}
                        </span>
                      </button>
                    </>
                  )}
                  {canManage && group.isManual && (
                    <button
                      type="button"
                      className="monitoring-delete-button"
                      title="Delete manual service"
                      aria-label={`Delete ${group.application}`}
                      onClick={() => confirmDeleteService(group)}
                      disabled={updatingKey === group.key || deleting}
                    >
                      <Icon name="trash" size="small" className="monitoring-delete-icon" />
                    </button>
                  )}
                </div>
              </div>
              {expanded && (
                <>
                  {group.appId && (
                    <div className="monitoring-appid">App ID: {group.appId}</div>
                  )}
                  {renderCredentialRows(group)}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!canViewSimple) {
    return (
      <div className="monitoring-container">
        <div className="monitoring-error">You don't have permission to view monitoring.</div>
      </div>
    );
  }

  return (
    <div className="monitoring-container">
      <div className="monitoring-header">
        <div>
          <h2>Monitoring</h2>
          <p>
            {isSetupView
              ? 'Enable expiry alerts for applications, set a threshold in days, and add manual credentials.'
              : 'Applications currently enabled for expiry monitoring.'}
          </p>
        </div>
        <div className="monitoring-header-actions">
          <div className="monitoring-search">
            <Icon name="search" size="small" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search applications"
            />
          </div>
          {canManage && isSetupView && (
            <button type="button" className="monitoring-add-button" onClick={openManualModal}>
              <Icon name="add" size="small" />
              Add manual entry
            </button>
          )}
        </div>
      </div>

      {canManage && (
        <div className="monitoring-tabs">
          <button
            type="button"
            className={`monitoring-tab ${activeView === 'setup' ? 'active' : ''}`}
            onClick={() => setActiveView('setup')}
          >
            Setup
          </button>
          <button
            type="button"
            className={`monitoring-tab ${activeView === 'monitored' ? 'active' : ''}`}
            onClick={() => setActiveView('monitored')}
          >
            Monitored
          </button>
        </div>
      )}

      <div className="monitoring-content">
        {renderGroups()}
      </div>

      {thresholdModal && (
        <div className="monitoring-modal-overlay" onClick={() => setThresholdModal(null)}>
          <form
            className="monitoring-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitThresholdModal}
          >
            <h3>{thresholdModal.mode === 'enable' ? 'Enable monitoring' : 'Alert threshold'}</h3>
            <p>
              How many days before expiry should alerts start for{' '}
              <strong>{thresholdModal.group.application}</strong>?
            </p>
            {thresholdModal.error && <div className="monitoring-modal-error">{thresholdModal.error}</div>}
            <label>
              Threshold (days)
              <input
                type="number"
                min="0"
                step="1"
                value={thresholdModal.days}
                onChange={(event) => setThresholdModal((prev) => ({ ...prev, days: event.target.value, error: '' }))}
                autoFocus
              />
            </label>
            <div className="monitoring-modal-actions">
              <button type="button" className="monitoring-modal-cancel" onClick={() => setThresholdModal(null)}>
                Cancel
              </button>
              <button type="submit" className="monitoring-modal-submit">
                {thresholdModal.mode === 'enable' ? 'Enable' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showManualModal && (
        <div className="monitoring-modal-overlay" onClick={() => setShowManualModal(false)}>
          <form
            className="monitoring-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitManualEntry}
          >
            <h3>Add manual entry</h3>
            {manualError && <div className="monitoring-modal-error">{manualError}</div>}
            <label>
              Application
              <input
                type="text"
                value={manualForm.application}
                onChange={(event) => setManualForm((prev) => ({ ...prev, application: event.target.value }))}
                placeholder="Application name"
                required
              />
            </label>
            <label>
              Type
              <select
                value={manualForm.type}
                onChange={(event) => setManualForm((prev) => ({ ...prev, type: event.target.value }))}
              >
                {MANUAL_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              Expiry date
              <input
                type="date"
                value={manualForm.expiry}
                onChange={(event) => setManualForm((prev) => ({ ...prev, expiry: event.target.value }))}
                required
              />
            </label>
            <p className="monitoring-modal-hint">Source will be saved as Manually.</p>
            <div className="monitoring-modal-actions">
              <button type="button" className="monitoring-modal-cancel" onClick={() => setShowManualModal(false)}>
                Cancel
              </button>
              <button type="submit" className="monitoring-modal-submit" disabled={manualSaving}>
                {manualSaving ? 'Saving…' : 'Add entry'}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteModal && (
        <div className="monitoring-modal-overlay" onClick={() => !deleting && setDeleteModal(null)}>
          <form
            className="monitoring-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitDeleteService}
          >
            <h3>Delete service</h3>
            <p>
              Remove <strong>{deleteModal.group.application}</strong> from monitoring? This cannot be undone.
            </p>
            {deleteModal.error && <div className="monitoring-modal-error">{deleteModal.error}</div>}
            <div className="monitoring-modal-actions">
              <button
                type="button"
                className="monitoring-modal-cancel"
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="submit" className="monitoring-modal-submit danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Monitoring;
